import React, { useState, useEffect } from 'react';

/**
 * VersionLabel Component
 * Displays version information from version.json in the bottom-right corner
 * Shows warning if version is not properly tagged or has uncommitted changes
 */
const VersionLabel = () => {
  const [versionInfo, setVersionInfo] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Fetch version information (cache-busting query param ensures fresh data)
    fetch(`/version.json?t=${Date.now()}`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Version file not found');
        }
        return response.json();
      })
      .then(data => {
        setVersionInfo(data);
      })
      .catch(err => {
        console.warn('Could not load version information:', err);
        setError(true);
      });
  }, []);

  if (error || !versionInfo) {
    return null; // Don't show anything if version info is unavailable
  }

  // Determine display text and styling
  const isValid = versionInfo.validForProduction;
  
  // Generate appropriate warning message
  let displayText;
  let detailText = '';
  
  if (isValid) {
    displayText = versionInfo.version;
  } else {
    displayText = 'Non-labeled version, not for testing';
    
    // Provide specific reason
    if (!versionInfo.hasExactTag && !versionInfo.isClean) {
      detailText = '(no tag on commit + uncommitted changes)';
    } else if (!versionInfo.hasExactTag) {
      detailText = '(no version tag on current commit)';
    } else if (!versionInfo.isClean) {
      detailText = `(${versionInfo.cleanStatus})`;
    }
  }

  const styles = {
    container: {
      position: 'fixed',
      top: '10px',
      left: '10px',
      fontFamily: 'monospace',
      fontSize: '10px',
      padding: '4px 8px',
      borderRadius: '3px',
      backgroundColor: isValid ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 165, 0, 0.7)',
      color: isValid ? 'rgba(255, 255, 255, 0.7)' : '#000',
      zIndex: 9999,
      userSelect: 'none',
      pointerEvents: 'none',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '2px',
      maxWidth: '250px',
      textAlign: 'left',
    },
    mainText: {
      fontWeight: isValid ? 'normal' : 'bold',
      whiteSpace: 'nowrap',
    },
    detailText: {
      fontSize: '8px',
      opacity: 0.8,
      whiteSpace: 'nowrap',
    },
  };

  return (
    <div style={styles.container} title={`Commit: ${versionInfo.commit}\nBranch: ${versionInfo.branch}\nExact tag: ${versionInfo.hasExactTag ? 'Yes' : 'No'}\nBuild: ${versionInfo.buildDate}`}>
      <div style={styles.mainText}>{displayText}</div>
      {!isValid && detailText && (
        <div style={styles.detailText}>{detailText}</div>
      )}
    </div>
  );
};

export default VersionLabel;
